"use client";

import { use, useState, useMemo } from "react";
import Link from "next/link";
import { notFound } from "next/navigation";
import { ArrowLeft, ChevronRight, Info } from "lucide-react";
import { getTool, CATEGORY_MAP } from "@/lib/tools/registry";
import { CalcInput } from "@/components/tools/calc-input";
import { ResultCard } from "@/components/tools/result-card";
import { DonutChart } from "@/components/tools/donut-chart";
import { FormulaDisplay } from "@/components/tools/formula-display";
import { FAQAccordion } from "@/components/tools/faq-accordion";
import { RelatedTools } from "@/components/tools/related-tools";
import { ScanCtaBanner, ScanCtaSidebar } from "@/components/tools/scan-cta-banner";

export default function ToolPage({
  params,
}: {
  params: Promise<{ category: string; slug: string }>;
}) {
  const { category, slug } = use(params);
  const tool = getTool(category, slug);

  if (!tool) {
    notFound();
  }

  const categoryMeta = CATEGORY_MAP[tool.category];
  const [values, setValues] = useState<Record<string, number | string | boolean>>(
    {}
  );

  // Initialize with defaults
  useMemo(() => {
    const defaults: Record<string, number | string | boolean> = {};
    tool.inputs.forEach((input) => {
      defaults[input.key] = input.default;
    });
    setValues(defaults);
  }, [tool]);

  // Calculate results
  const results = useMemo(() => {
    try {
      return tool.calculate(values);
    } catch (error) {
      console.error("Calculation error:", error);
      return null;
    }
  }, [values, tool]);

  const handleInputChange = (key: string, value: number | string | boolean) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors">
      {/* Breadcrumb */}
      <div className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm">
            <Link
              href="/"
              className="text-slate-600 transition-colors hover:text-brand dark:text-slate-400"
            >
              Home
            </Link>
            <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-600" />
            <Link
              href="/tools"
              className="text-slate-600 transition-colors hover:text-brand dark:text-slate-400"
            >
              Tools
            </Link>
            <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-600" />
            <Link
              href={`/tools/${category}`}
              className="text-slate-600 transition-colors hover:text-brand dark:text-slate-400"
            >
              {categoryMeta?.title}
            </Link>
            <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-600" />
            <span className="font-medium text-slate-900 dark:text-white">
              {tool.title}
            </span>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white dark:border-slate-800 dark:from-slate-900 dark:to-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          <Link
            href={`/tools/${category}`}
            className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-brand transition-all hover:gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {categoryMeta?.shortTitle} Calculators
          </Link>
          <h1 className="mb-2 text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl">
            {tool.title}
          </h1>
          <p className="max-w-2xl text-lg text-slate-600 dark:text-slate-300">
            {tool.description}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Input Section */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900 space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {tool.inputs.length > 0 ? "Enter Values" : "Calculator"}
              </h2>

              {tool.inputs.length > 0 ? (
                <div className="space-y-6">
                  {tool.inputs.map((input) => (
                    <CalcInput
                      key={input.key}
                      input={input}
                      value={values[input.key]}
                      onChange={(value) => handleInputChange(input.key, value)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  This calculator runs automatically. Results shown on the right.
                </p>
              )}
            </div>
          </div>

          {/* Results + Chart Section */}
          <div className="lg:col-span-2 space-y-8">
            {results ? (
              <>
                {/* Result Cards */}
                <div>
                  <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">
                    Results
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {tool.resultFields.map((field) => (
                      <ResultCard
                        key={field.key}
                        field={field}
                        value={results[field.key] ?? 0}
                      />
                    ))}
                  </div>
                </div>

                {/* Chart */}
                {!tool.hideChart && tool.chartSegments && (
                  <div>
                    <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">
                      Breakdown
                    </h2>
                    <DonutChart
                      segments={tool.chartSegments.map((seg) => ({
                        label: seg.label,
                        value: typeof results[seg.valueKey] === "number" ? (results[seg.valueKey] as number) : typeof values[seg.valueKey] === "number" ? (values[seg.valueKey] as number) : 0,
                        color: seg.color,
                      }))}
                    />
                  </div>
                )}

              </>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-800 dark:bg-slate-900/50">
                <p className="text-slate-600 dark:text-slate-400">
                  Fill in the values to see results
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Sections + Sticky Sidebar CTA */}
      <div className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-10">
            {/* LEFT: Content */}
            <div className="space-y-12 min-w-0">
              {/* What Is Section */}
              {tool.content.whatIs && (
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
                    What is {tool.shortTitle}?
                  </h2>
                  <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-slate-600 dark:prose-p:text-slate-400 prose-headings:text-slate-900 dark:prose-headings:text-white">
                    <p className="text-slate-700 dark:text-slate-300">
                      {tool.content.whatIs}
                    </p>
                  </div>
                </div>
              )}

              {/* How To Use Section */}
              {tool.content.howToUse && (
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
                    How to Use This Calculator
                  </h2>
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <p className="text-slate-700 dark:text-slate-300">
                      {tool.content.howToUse}
                    </p>
                  </div>
                </div>
              )}

              {/* Formula Section */}
              {tool.content.formula && (
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
                    Formula
                  </h2>
                  <FormulaDisplay formula={tool.content.formula} />
                </div>
              )}

              {/* Benefits Section */}
              {tool.content.benefits && tool.content.benefits.length > 0 && (
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
                    Benefits & Key Points
                  </h2>
                  <ul className="space-y-3">
                    {tool.content.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="mt-1 inline-block h-2 w-2 rounded-full bg-brand flex-shrink-0" />
                        <span className="text-slate-700 dark:text-slate-300">
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Custom Content Sections */}
              {tool.content.sections &&
                tool.content.sections.map((section, idx) => (
                  <div key={idx}>
                    <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
                      {section.title}
                    </h2>
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: section.body }}
                    />
                  </div>
                ))}

              {/* FAQs — FAQAccordion renders its own h2 header */}
              {tool.content.faqs && tool.content.faqs.length > 0 && (
                <FAQAccordion faqs={tool.content.faqs} />
              )}

              {/* Inline CTA (mobile only) */}
              <div className="lg:hidden">
                <ScanCtaBanner documentType={tool.documentType} />
              </div>
            </div>

            {/* RIGHT: Sticky sidebar CTA (desktop only) */}
            <aside className="hidden lg:block self-stretch">
              <div className="sticky top-24">
                <ScanCtaSidebar documentType={tool.documentType} />
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Related Tools */}
      {tool.content.relatedSlugs && tool.content.relatedSlugs.length > 0 && (() => {
        const relatedTools = tool.content.relatedSlugs
          .map((s) => {
            const [cat, sl] = s.split("/");
            const t = getTool(cat, sl);
            if (!t) return null;
            const catMeta = CATEGORY_MAP[t.category];
            return { title: t.title, shortTitle: t.shortTitle, description: t.description, href: `/tools/${t.category}/${t.slug}`, categoryTitle: catMeta?.title ?? t.category };
          })
          .filter(Boolean) as { title: string; shortTitle: string; description: string; href: string; categoryTitle: string }[];
        if (relatedTools.length === 0) return null;
        return (
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
            <RelatedTools tools={relatedTools} />
          </div>
        );
      })()}

      {/* Disclaimer */}
      <div className="border-t border-slate-200 bg-amber-50 dark:border-slate-800 dark:bg-amber-900/10">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex gap-4">
            <Info className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h3 className="mb-2 font-semibold text-amber-900 dark:text-amber-300">
                Disclaimer
              </h3>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                This calculator is provided for informational and educational
                purposes only. While we strive for accuracy, results should be
                verified with official sources or by consulting qualified
                professionals. Tax laws, rates, and regulations are subject to
                change. GotRedFlags is not responsible for financial decisions
                made based on these tools.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
